import {makeAutoObservable, runInAction} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {ChannelExpandedCommon} from '../../../../api/channel';
import type {StrategyByType} from './StrategyByType';
import type {Config} from './types';

import {channelExpandCommon, channelStore} from '../../../../api/channel';
import {currentUserStore} from '../../../../api/user';
import {isMeCoachForEntity} from '../../../../utils/entity/isMeCoachForEntity';
import {isDefined} from '../../../../utils/typecheck';
import {
  ChannelCourseStrategy,
  ChannelDefaultStrategy,
  ChannelProgramStrategy,
  ChannelSupportStrategy,
} from './StrategyByType';

export class ChannelInfoStore {
  channel: ChannelExpandedCommon | null = null;
  channelId: Config['channelId'];
  strategyByType: StrategyByType;

  constructor(config: Config) {
    this.channelId = config.channelId;
    this.channel = config.channel || null;

    makeAutoObservable(
      this,
      {},
      {
        autoBind: true,
      },
    );

    if (isDefined(this.channel)) {
      this.strategyByType = this.createStrategyByType(this.channel);
    } else {
      this.refresh();
    }
  }

  get avatar() {
    return this.channel ? this.strategyByType.getAvatar(this.channel) : null;
  }

  get clientName() {
    const clientName = this.channel
      ? this.strategyByType.getClientName(this.channel)
      : null;

    return clientName ?? i18n.t('shared:client_not_found');
  }

  get clientSource() {
    return this.channel
      ? this.strategyByType.getClientSource(this.channel)
      : null;
  }

  get description() {
    return this.channel ? this.strategyByType.getDescription(this.channel) : '';
  }

  get isArchived() {
    return this.channel
      ? this.strategyByType.getIsArchived(this.channel)
      : false;
  }

  get isCorporate() {
    return this.channel
      ? this.strategyByType.getIsCorporate(this.channel)
      : false;
  }

  get isEnded() {
    return this.channel ? this.strategyByType.getIsEnded(this.channel) : false;
  }

  get isIndividual() {
    return this.channel
      ? this.strategyByType.getIsIndividual(this.channel)
      : false;
  }

  get isNewClient() {
    return this.channel
      ? this.strategyByType.getIsNewClient(this.channel)
      : false;
  }

  get isOneToOne() {
    if (this.channel) {
      return this.strategyByType.getIsOneToOne(this.channel);
    }

    return false;
  }

  get isStarted() {
    return this.channel
      ? this.strategyByType.getIsStarted(this.channel)
      : false;
  }

  get title() {
    return this.channel ? this.strategyByType.getTitle(this.channel) : '...';
  }

  get userIsCoach() {
    return isMeCoachForEntity(this.channel?.resource);
  }

  get userIsFrozen() {
    return this.channel
      ? this.strategyByType.getUserIsFrozen(this.channel, this.userIsCoach)
      : false;
  }

  get userIsOwner() {
    return this.channel?.user_id === currentUserStore.user?._id;
  }

  async refresh() {
    const channel = (await channelStore.fetch({
      _id: this.channelId,
      expand: channelExpandCommon,
    })) as ChannelExpandedCommon;

    runInAction(() => {
      this.channel = channel;
      this.strategyByType = this.createStrategyByType(channel);
    });
  }

  private createStrategyByType(channel: ChannelExpandedCommon) {
    switch (channel.type) {
      case 'course':
        return new ChannelCourseStrategy();
      case 'program':
        return new ChannelProgramStrategy();
      case 'support':
        return new ChannelSupportStrategy();
      default:
        return new ChannelDefaultStrategy();
    }
  }
}
